import {
  authenticatedFetch,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import { buildApiError } from './api-error';
import {
  CaseBuildChecklistSummaryDto,
  CaseBuildChecklistSummaryDtoSchema,
  CaseBuildStatus,
  CaseEditingSessionDto,
  CaseEditingSessionDtoSchema,
  CasePaginationParams,
  CaseResponseDto,
  CaseResponseDtoSchema,
  CasesResponseDTO,
  PreferenceCardResponseDto,
  PreferenceCardResponseDtoSchema,
  SessionStatusResponseDto,
  SessionStatusResponseDtoSchema,
  UpdateBuildStatusDto,
  UpdateDispatchStatusDto,
  VerifyAssetsDto,
  UpdateSidehubAvailableStatusDto,
  UpdateSuppliesAndInstrumentsDto,
  UpdateTrackedItemsDto,
  AssignTrackedItemsBatchDto,
  ReserveTrackedItemsBatchDto,
} from '@peritasai/types';
import { z } from 'zod';
type BaseUrlResolver = () => string;

/**
 * Response DTO for sidehub/sidehub endpoint
 */
const SidehubLocationDetailsDtoSchema = z.object({
  locationDisplayName: z.string(),
  assetLifecycleFunction: z.string(),
});
export type SidehubLocationDetailsDto = z.infer<
  typeof SidehubLocationDetailsDtoSchema
>;

const SidehubResponseDtoSchema = z.object({
  id: z.string(),
  /** Hub is added to whitelist */
  enabled: z.boolean(),
  /** Hub is connected */
  active: z.boolean(),
  /** Hub is available */
  available: z.boolean(),
  lastUpload: z.string().nullish(),
  minSignal: z.number().nullish(),
  deviceId: z.string(),
  type: z.union([
    z.literal('sidetooth'),
    z.literal('sidewinder'),
    z.literal('sidekick'),
  ]),
  function: z.union([
    z.literal('dispatch'),
    z.literal('checkIn'),
    z.literal('operatingRoom'),
    z.literal('rack'),
  ]),
  address: z.string().nullish(),
  controller: z.boolean(),
  locationId: z.string().nullish(),
  location: z
    .object({ id: z.string(), name: z.string() })
    .nullish(),
  locationDetails: SidehubLocationDetailsDtoSchema.nullish(),
  positionX: z.number(),
  positionY: z.number(),
  positionZ: z.number(),
  /** Creation timestamp (ISO date-time string) */
  createdAt: z.string(),
  /** Last update timestamp (ISO date-time string) */
  updatedAt: z.string(),
});
export type SidehubResponseDto = z.infer<typeof SidehubResponseDtoSchema>;

/**
 * The API returns a paginated envelope; we transform to {@link CasesResponseDTO}
 * (with `cases`/`hasMore`/`pageSize`) for consumer convenience.
 */
const PaginatedCasesApiResponseSchema = z.object({
  data: z.array(CaseResponseDtoSchema),
  total: z.number(),
  page: z.number().optional(),
  limit: z.number().optional(),
  statistics: z
    .object({
      totalCases: z.number(),
      activeCases: z.number(),
      completedCases: z.number(),
      notStartedCases: z.number(),
    })
    .optional(),
});

/**
 * Low-level API client for case schedule data
 * This would typically make HTTP requests to a REST API
 */
export class CaseScheduleApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getCase(
    caseId: string,
    options?: { includeChecklistItems?: boolean }
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const queryParams = new URLSearchParams();

    if (options?.includeChecklistItems) {
      queryParams.append('includeChecklistItems', 'false');
    }

    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error fetching case:', error);
      throw error;
    }
  }
  async getPreferenceCard(
    preferenceCardId: string
  ): Promise<PreferenceCardResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const queryParams = new URLSearchParams();

    const url = `${baseUrl}/preference-cards/${encodeURIComponent(preferenceCardId)}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }
      return PreferenceCardResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error('Error fetching preference card:', error);
      throw error;
    }
  }

  async getCases(options: CasePaginationParams): Promise<CasesResponseDTO> {
    const queryParams = new URLSearchParams();

    // Map internal options to API query params
    if (options.page !== undefined) {
      // Ensure page is 1-based for the API (if input is 0, send 1)
      const pageToSend = options.page === 0 ? 1 : options.page;
      queryParams.append('page', pageToSend.toString());
    }
    if (options.pageSize !== undefined) {
      queryParams.append('limit', options.pageSize.toString());
    }

    // Handle dates - default to current month if not provided as they are required by backend
    let startDate = options.startDate;
    let endDate = options.endDate;

    if (!startDate || !endDate) {
      // Default to current month based on typical usage if not provided
      const now = new Date();
      if (!startDate) {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate = start.toISOString();
      }
      if (!endDate) {
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        endDate = end.toISOString();
      }
    }

    if (startDate) {
      const decoded = decodeURIComponent(startDate);
      queryParams.append('startDate', decoded);
    }
    if (endDate) {
      const decoded = decodeURIComponent(endDate);
      queryParams.append('endDate', decoded);
    }

    if (options.locationId !== undefined) {
      queryParams.append('locationId', options.locationId);
    }

    if (options.includeChecklistItems !== undefined) {
      queryParams.append(
        'includeChecklistItems',
        options.includeChecklistItems.toString()
      );
    }

    const baseUrl = this.resolveBaseUrl();
    // toString() of URLSearchParams automatically encodes values
    const url = `${baseUrl}/cases${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        // Log the error text to help debugging
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const parsed = PaginatedCasesApiResponseSchema.safeParse(
        await response.json()
      );
      if (!parsed.success) {
        console.error('Invalid cases response shape:', parsed.error.issues);
        throw new Error('Failed to parse cases response');
      }
      const data = parsed.data;

      const page = data.page ?? options.page ?? 1;
      const pageSize = data.limit ?? options.pageSize ?? 50;
      const hasMore = page * pageSize < data.total;

      // Convert scheduledDateTime strings to Date objects
      const casesWithDates: CaseResponseDto[] = data.data.map((c) => ({
        ...c,
        scheduledDateTime:
          typeof c.scheduledDateTime === 'string'
            ? new Date(c.scheduledDateTime)
            : c.scheduledDateTime,
      }));

      return {
        cases: casesWithDates,
        total: data.total,
        hasMore,
        page,
        pageSize,
        statistics: data.statistics,
      };
    } catch (error) {
      console.error('Error fetching cases:', error);
      throw error;
    }
  }

  async updateTrackedItems(
    caseId: string,
    trackedItems: UpdateTrackedItemsDto
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/tracked-items/assign`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(trackedItems),
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error updating tracked items:', error);
      throw error;
    }
  }

  async unassignTrackedItem(
    caseId: string,
    trackedItem: UpdateTrackedItemsDto
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/tracked-items/assign`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(trackedItem),
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error unassigning tracked item:', error);
      throw error;
    }
  }

  async updateTrackedItemsBatch(
    caseId: string,
    dto: AssignTrackedItemsBatchDto
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/tracked-items/assign-batch`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error updating tracked items batch:', error);
      throw error;
    }
  }

  async reserveTrackedItemsBatch(
    caseId: string,
    dto: ReserveTrackedItemsBatchDto
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/tracked-items/reserve-batch`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error reserving tracked items batch:', error);
      throw error;
    }
  }

  async updateBuildStatus(
    caseId: string,
    buildStatus: CaseBuildStatus
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/build-status`;

    try {
      const requestBody: UpdateBuildStatusDto = { buildStatus };

      const response = await authenticatedFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error updating build status:', error);
      throw error;
    }
  }

  async updateDispatchStatus(
    caseId: string,
    dispatchStatus: boolean
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/dispatch-status`;

    try {
      const requestBody: UpdateDispatchStatusDto = { dispatchStatus };

      const response = await authenticatedFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error updating dispatch status:', error);
      throw error;
    }
  }

  async verifyCaseAssets(
    caseId: string,
    verifiedBy: string
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/verify-assets`;

    try {
      const requestBody: VerifyAssetsDto = { verifiedBy };

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error verifying case assets:', error);
      throw error;
    }
  }

  async updateSuppliesAndInstruments(
    caseId: string,
    dto: UpdateSuppliesAndInstrumentsDto
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/supplies-and-single-packed-instruments`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error updating supplies and instruments:', error);
      throw error;
    }
  }

  async updateUnexpectedItem(
    caseId: string,
    itemId: string
  ): Promise<CaseResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/unexpected-item/${encodeURIComponent(itemId)}/confirm`;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw await buildApiError(response);
      }

      const data = CaseResponseDtoSchema.parse(await response.json());

      // Convert scheduledDateTime string to Date if needed
      return {
        ...data,
        scheduledDateTime:
          typeof data.scheduledDateTime === 'string'
            ? new Date(data.scheduledDateTime)
            : data.scheduledDateTime,
      };
    } catch (error) {
      console.error('Error confirming unexpected item:', error);
      throw error;
    }
  }

  async startEditingSession(caseId: string): Promise<CaseEditingSessionDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/editing-session/start`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw await buildApiError(response);
    }

    return CaseEditingSessionDtoSchema.parse(await response.json());
  }

  async extendEditingSession(caseId: string): Promise<CaseEditingSessionDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/editing-session/extend`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(
        `HTTP error! status: ${response.status} - ${response.statusText}`
      );
    }

    return CaseEditingSessionDtoSchema.parse(await response.json());
  }

  async endEditingSession(caseId: string): Promise<void> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/editing-session/end`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(
        `HTTP error! status: ${response.status} - ${response.statusText}`
      );
    }
    // End endpoint returns void, no response body to parse
  }

  async getEditingSessionStatus(
    caseId: string
  ): Promise<SessionStatusResponseDto | null> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/editing-session/status`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (response.status === 404 || response.status === 204) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(
        `HTTP error! status: ${response.status} - ${response.statusText}`
      );
    }

    return SessionStatusResponseDtoSchema.parse(await response.json());
  }

  async getCaseBuildChecklist(
    caseId: string
  ): Promise<CaseBuildChecklistSummaryDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/cases/${encodeURIComponent(caseId)}/case-build-checklist`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      return CaseBuildChecklistSummaryDtoSchema.parse(await response.json());
    } catch (error) {
      console.error('Error fetching case build checklist:', error);
      throw error;
    }
  }

  async getSideHubs(): Promise<SidehubResponseDto[]> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/sidehubs`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      return z.array(SidehubResponseDtoSchema).parse(await response.json());
    } catch (error) {
      console.error('Error fetching sidehubs:', error);
      throw error;
    }
  }

  async updateSidehubAvailableStatus(
    sidehubId: string,
    available: boolean
  ): Promise<void> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/sidehubs/${encodeURIComponent(sidehubId)}/available-status`;

    try {
      const requestBody: UpdateSidehubAvailableStatusDto = { available };

      const response = await authenticatedFetch(url, {
        method: 'PUT',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }
    } catch (error) {
      console.error('Error updating sidehub available status:', error);
      throw error;
    }
  }
}

export const createCaseScheduleApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): CaseScheduleApiClient => new CaseScheduleApiClient(resolveBaseUrl);

export const caseScheduleApiClient = new CaseScheduleApiClient();
